const csrfStorageKey = "matrix_studio_csrf_token";
const fallbackProductionBaseURL = "https://newai-api.yuezecloud.com";
export const loginDialogEventName = "matrix-studio:open-login-dialog";
let csrfRefreshPromise = null;

export class ApiError extends Error {
  constructor(message, { code = "request_failed", status = 500 } = {}) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

export function isUnauthorizedError(error) {
  return error instanceof ApiError && Number(error.status || 0) === 401;
}

export function requestLoginDialog() {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new Event(loginDialogEventName));
}

export function loadCSRFToken() {
  return sessionStorage.getItem(csrfStorageKey) || "";
}

export function saveCSRFToken(token) {
  if (!token) {
    sessionStorage.removeItem(csrfStorageKey);
    return;
  }
  sessionStorage.setItem(csrfStorageKey, token);
}

export function clearCSRFToken() {
  sessionStorage.removeItem(csrfStorageKey);
}

function isSafeMethod(method) {
  return ["GET", "HEAD", "OPTIONS"].includes((method || "GET").toUpperCase());
}

function isInvalidCSRFResponse(response, payload) {
  return Number(response && response.status || 0) === 403
    && payload
    && payload.error
    && payload.error.code === "invalid_csrf_token";
}

export function buildAPIURL(path) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const baseURL = (process.env.VUE_APP_API_BASE_URL || "").replace(/\/+$/, "");

  if (baseURL) {
    return `${baseURL}${normalizedPath}`;
  }

  if (
    typeof window !== "undefined"
    && ["newai.yuezecloud.com", "ai.yuezecloud.com"].includes(window.location.hostname)
  ) {
    return `${fallbackProductionBaseURL}${normalizedPath}`;
  }

  return normalizedPath;
}

export async function refreshCSRFToken() {
  if (csrfRefreshPromise) {
    return csrfRefreshPromise;
  }
  csrfRefreshPromise = fetch(buildAPIURL("/api/v1/auth/session"), {
    method: "GET",
    credentials: "include",
  })
    .then(async (response) => {
      const contentType = response.headers.get("content-type") || "";
      const payload = contentType.includes("application/json") ? await response.json() : null;
      if (response.ok && payload && payload.authenticated && payload.csrf_token) {
        saveCSRFToken(payload.csrf_token);
        return payload.csrf_token;
      }
      clearCSRFToken();
      return "";
    })
    .catch(() => {
      clearCSRFToken();
      return "";
    })
    .finally(() => {
      csrfRefreshPromise = null;
    });
  return csrfRefreshPromise;
}

async function performAPIRequest(path, options, method, headers) {
  const url = buildAPIURL(path);
  const response = await fetch(url, {
    ...options,
    method,
    headers,
    credentials: "include",
  });

  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json") ? await response.json() : null;
  if (payload && payload.csrf_token) {
    saveCSRFToken(payload.csrf_token);
  }
  return { response, payload };
}

export async function apiRequest(path, options = {}) {
  const method = (options.method || "GET").toUpperCase();
  const headers = new Headers(options.headers || {});

  if (!headers.has("Content-Type") && options.body && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  if (!isSafeMethod(method)) {
    let csrfToken = loadCSRFToken();
    if (!csrfToken) {
      csrfToken = await refreshCSRFToken();
    }
    if (csrfToken) {
      headers.set("X-CSRF-Token", csrfToken);
    }
  }

  let { response, payload } = await performAPIRequest(path, options, method, headers);
  if (!isSafeMethod(method) && isInvalidCSRFResponse(response, payload)) {
    const csrfToken = await refreshCSRFToken();
    if (csrfToken) {
      headers.set("X-CSRF-Token", csrfToken);
      ({ response, payload } = await performAPIRequest(path, options, method, headers));
    }
  }

  if (!response.ok) {
    const error = payload && payload.error ? payload.error : {};
    throw new ApiError(error.message || `Request failed with status ${response.status}`, {
      code: error.code || "request_failed",
      status: response.status,
    });
  }

  return payload;
}
